import type { StateCreator } from "zustand/vanilla";

/**
 * ShareTarget is the itinerary the share-link modal is open for. The modal carries
 * only the itinerary id; the share token itself it mints (and rotates) through the
 * REST layer and holds in its own local state, so the store slot stays a minimal
 * pointer rather than a mirror of the whole modal.
 */
export interface ShareTarget {
  /** itineraryId is the itinerary the share link is being minted for. */
  itineraryId: string;
}

/**
 * ShareSliceState is the open share-link modal slot.
 */
export interface ShareSliceState {
  /**
   * share is the open share-link modal state (null = the modal is closed). It holds
   * only the itinerary the link is minted for; the modal's token lives in its own
   * local state, so this slot stays a minimal open/closed pointer. Set only through
   * the tool bus, like every other UiAction.
   */
  share: ShareTarget | null;
}

/**
 * ShareSliceActions is the whole-slot setter that drives the share-link modal,
 * reached only through the tool bus.
 */
export interface ShareSliceActions {
  /**
   * setShare opens the share-link modal with the given target, or closes it when
   * passed null. The whole-slot setter the open/close tools drive; the modal has no
   * narrower fields in the store (its token lives in the surface's local state), so
   * this is the slice's only action.
   */
  setShare: (share: ShareTarget | null) => void;
}

/**
 * shareInitialState seeds the share-link modal slot closed.
 */
export const shareInitialState: ShareSliceState = {
  share: null,
};

/**
 * createShareSlice builds the share-link modal action. The single whole-slot setter
 * opens the modal for an itinerary or closes it, mirroring the embed slice's
 * open/close shape without any of the wizard-progress fields (the share modal keeps
 * its minted token in its own local state, not the store).
 */
export const createShareSlice: StateCreator<
  ShareSliceState & ShareSliceActions,
  [],
  [],
  ShareSliceActions
> = (set) => ({
  setShare: (share) => set({ share }),
});

/**
 * selectShare is the open share-link modal state, or null when the modal is closed.
 * The mount-guard selector the share modal reads to decide whether to render at all —
 * narrow so the guard re-renders only on the open/close flip, mirroring selectEmbed.
 */
export const selectShare = (state: ShareSliceState): ShareTarget | null => state.share;
